import consola from 'consola'
import moment from 'moment'
import { uuidv7 } from 'posthog-js/lib/src/uuidv7'
import { MethodologyEnum, type PlanItemPosFormulaBased, type PlanPosItem, type PlanSubPosItem } from '~/protos/gen/stcs_comp_plan_cfg.entity'
import { StcsDisplayValueTypeEnum, StcsFpnaTypeEnum, StcsFstmtEnum } from '~/protos/gen/stcs_enums.entity'
import { getRowDisplayName } from '~/services/commons.service'
import { useCtxStore } from '~/stores/ctx.store.ts'
import { createPlanningViewStore } from '~/stores/planningview.store'
import type { FormulaOffset, FormulaOperand, FormulaToken } from '~/types/formula-based'
import { createMonthlyGrowthPosEntry } from './monthly-growth'
import { getSelectedPlanRowDataByFstmt } from './planning-helpers'

//--------------------- PAYLOAD HANDLING PART -----------------------------
// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
export const createFormulaBasedPosEntry = (row: PlanningRowData, isSubPos = false, isStatusUpdate = false, parentRow?: PlanningRowData): PlanPosItem => {
  const planningStore = createPlanningViewStore(useCtxStore().active_comp_id)
  const selectedPlan = planningStore.selectedPlanState

  const formula_based_input_list = getFormulaBasedInputList(row.formulaTokens || [], row)
  let sub_pos_map = {}
  let pos_input_base_amount = 0

  if (isSubPos) {
    const baseAmountKey = moment(selectedPlan?.fpna_begin_date_string, 'YYYY-MM').subtract(1, 'months').format('YYYY-MM')
    const valuesMap = createValidInputManualMap(row)
    pos_input_base_amount = valuesMap?.[baseAmountKey]?.manual_number_input || 0
  } else {
    sub_pos_map = getSubPositionMap(row, isStatusUpdate)
  }

  // Get pos_res_plan_map from cache only when isStatusUpdate is true
  let pos_res_plan_map = {}
  if (isStatusUpdate && selectedPlan) {
    const IS_PNL = row.fstmt_type === StcsFstmtEnum.SFSE_PNL
    const mapKey = IS_PNL ? 'pnl_map' : 'bs_map'

    if (isSubPos && parentRow) {
      // For sub-positions, look in parent's formula_based.sub_pos_map
      const grandParentRowId = parentRow.path_level[parentRow.path_level.length - 2]
      pos_res_plan_map = planningStore.planConfigCache[selectedPlan.id || '']?.stcs_comp_plan_cfg?.[mapKey]?.[grandParentRowId]?.pos_map?.[parentRow.row_id]?.formula_based?.sub_pos_map?.[row.row_id]?.formula_based?.pos_res_plan_map || {}
    } else {
      // For main positions, look in pos_map
      const parentRowId = row.path_level[row.path_level.length - 2]
      pos_res_plan_map = planningStore.planConfigCache[selectedPlan.id || '']?.stcs_comp_plan_cfg?.[mapKey]?.[parentRowId]?.pos_map?.[row.row_id]?.formula_based?.pos_res_plan_map || {}
    }
  }

  const formulaBased: PlanItemPosFormulaBased = {
    pos_input_base_amount,
    formula_based_input_list,
    pos_res_plan_map: pos_res_plan_map,
    sub_pos_map,
    status: {
      status_code: 1,
      manual_override_map: {},
      message: '',
      cycle_path_key_list: [],
    },
  }
  return {
    pos_logic_type: MethodologyEnum.FORMULA_BASED,
    display_value_type: row?.outputType || StcsDisplayValueTypeEnum.SDVTE_CURRENCY,
    fstmt_type: row.fstmt_type,
    formula_based: formulaBased,
    is_struct_item: false, // Formula entries are typically not structural items
    pos_index: 0, // Will be set properly when processing backend response
    status: undefined, // Handle by the backend only
  }
}

const getFormulaBasedInputList = (formulaTokens: FormulaToken[], row: PlanningRowData): PlanItemPosFormulaBased['formula_based_input_list'] => {
  const formulaBasedInputList: PlanItemPosFormulaBased['formula_based_input_list'] = []

  for (const token of formulaTokens) {
    // Handle mathematical operators and text (numbers, parentheses, etc.)
    if (token.type === 'text' && token.text) {
      formulaBasedInputList.push({ math_op: token.text })
      continue
    }

    // Handle operand tokens (references to other plan items)
    if (token.type === 'operand' && token.chip?.operand && token.chip?.offset) {
      const inputItem = convertOperandToInputItem(token.chip.operand, token.chip.offset, row)
      if (inputItem) {
        formulaBasedInputList.push(inputItem)
      }
    }
  }

  return formulaBasedInputList
}

const getSubPositionMap = (row: PlanningRowData, isStatusUpdate: boolean): { [key: string]: PlanSubPosItem } => {
  const subPositionRows = getSubPositionsOfParentRow(row)
  const subPosMap: { [key: string]: PlanSubPosItem } = {}
  let posIndex = 0

  for (const subPosRow of subPositionRows) {
    // Only process rows that have a selected methodology
    if (!subPosRow.selectedMethod) {
      continue
    }

    const subPosKey = subPosRow.row_id
    // Convert the row to a PlanSubPosItem based on its methodology
    const subPosItem = convertRowToSubPosItem(subPosRow, row, isStatusUpdate)

    if (subPosItem) {
      // Assign the incremental pos_index to the sub-position
      subPosItem.pos_index = posIndex
      posIndex++
      subPosMap[subPosKey] = subPosItem
    }
  }

  return subPosMap
}

export const getSubPositionsOfParentRow = (row: PlanningRowData): PlanningRowData[] => {
  const allRows = getSelectedPlanRowDataByFstmt(row.fstmt_type)
  const parentRowId = row.row_id

  // Find direct children by checking:
  // 1. Must be a sub-custom-row (all sub-positions have this class)
  // 2. Parent's row_id must be the second-to-last element in child's path_level
  //    (last element is always the child's own row_id)
  return allRows.filter((childRow) => {
    if (childRow.rowClass !== 'sub-custom-row') return false
    if (!childRow.path_level || childRow.path_level.length < 2) return false

    // Parent should be the second-to-last element (direct parent)
    const parentIndex = childRow.path_level.length - 2
    return childRow.path_level[parentIndex] === parentRowId
  })
}

// Converts a formula operand to the appropriate backend input item type
const convertOperandToInputItem = (operand: FormulaOperand, offset: FormulaOffset, row: PlanningRowData): PlanItemPosFormulaBased['formula_based_input_list'][number] | null => {
  const offsetInMonths = calculateOffsetInMonths(offset)
  // Handle self-referential references
  if (operand.isSelfReferential) {
    return {
      plan_ref_self_position: {
        offset_in_months: offsetInMonths,
      },
    }
  }

  // Handle position references (financial statement positions)
  if (operand.display_type === 'planning') {
    // Get parent row_id of the current row (where formula is being created)
    const currentRowParentId = getParentRowId(row.path_level)
    const operandRow = getRowByLevelId(operand.fstmt_type, operand.id)
    const operandParentId = getParentRowId(operandRow?.path_level || [])
    // If operand's parent differs from current row's parent, use plan_ref_level_2_position
    // This means we're referencing a sub-position from a different parent position
    if (operandParentId && currentRowParentId && operandParentId !== currentRowParentId) {
      return {
        plan_ref_level_2_position: {
          ref_id_level: extractLevel2Id(operandParentId),
          offset_in_months: offsetInMonths,
          fstmt_type: operand.fstmt_type,
          ref_id_plan_position: operand.id,
        },
      }
    }

    // If operand's parent is the same as current row's parent, use plan_ref_position
    // This means we're referencing a sibling sub-position
    return {
      plan_ref_position: {
        ref_id_plan_position: operand.id,
        offset_in_months: offsetInMonths,
        fstmt_type: operand.fstmt_type,
      },
    }
  }

  // Handle level-1/level-2/level-3/total references
  if (operand.display_type === 'level-1' || operand.display_type === 'level-2' || operand.display_type === 'level-3' || operand.display_type === 'total' || operand.display_type === 'subtotal') {
    return {
      plan_ref_fstmt: {
        fstmt_type: operand.fstmt_type,
        ref_id_level: operand.level_id,
        offset_in_months: offsetInMonths,
        fpna_type: operand?.fpna_type || StcsFpnaTypeEnum.SFT_LEVEL_2,
      },
    }
  }

  // Handle sub position references
  if (operand.display_type === 'subposition') {
    return {
      plan_ref_sub_position: {
        ref_id_plan_sub_position: operand.id,
        offset_in_months: offsetInMonths,
        fstmt_type: operand.fstmt_type,
      },
    }
  }

  return null
}

const calculateOffsetInMonths = (offset: FormulaOffset): number => {
  switch (offset.type) {
    case 'this_month': {
      return 0
    }
    case 'previous_month': {
      return -1
    }
    case 'custom': {
      // Parse custom months value (can be string or number)
      const customMonths = Number.parseInt(String(offset.customMonths || 0), 10)
      if (Number.isNaN(customMonths)) {
        return 0
      }
      return customMonths
    }
    default: {
      consola.warn(`Unknown offset type: ${offset.type}. Defaulting to 0.`)
      return 0
    }
  }
}

const convertRowToSubPosItem = (row: PlanningRowData, parentRow: PlanningRowData, isStatusUpdate: boolean): PlanSubPosItem | null => {
  const methodology = row?.selectedMethod

  if (!methodology) {
    return null
  }

  switch (methodology) {
    case MethodologyEnum.MANUAL_DEC_INPUT: {
      return createManualPosEntry(row, true, isStatusUpdate, parentRow)
    }
    case MethodologyEnum.MONTHLY_GROWTH_RATE: {
      return createMonthlyGrowthPosEntry(row, true, isStatusUpdate, parentRow)
    }
    case MethodologyEnum.FORMULA_BASED: {
      return createFormulaBasedPosEntry(row, true, isStatusUpdate, parentRow)
    }
    default: {
      consola.warn(`Unsupported methodology for sub-position: ${methodology}`)
      return null
    }
  }
}

//--------------------- RESPONSE HANDLING PART -----------------------------

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
export const handleFormulaBasedPositions = (positionKey: string, planPosValue: PlanPosItem) => {
  if (!planPosValue || planPosValue.pos_logic_type !== MethodologyEnum.FORMULA_BASED || !planPosValue.formula_based) {
    return
  }
  const rows = getSelectedPlanRowDataByFstmt(planPosValue.fstmt_type)
  const row = rows.find((r) => r.row_id === positionKey)

  if (!row) return

  const formulaBasedTokens = makeFormulaTokensFromPlanItem(planPosValue.formula_based.formula_based_input_list || [], row)
  row.formulaTokens = formulaBasedTokens
  row.calc_status = planPosValue.formula_based.status

  // Update the sub position values as well
  if (planPosValue.formula_based.sub_pos_map) {
    for (const [subPosKey, subPosValue] of Object.entries(planPosValue.formula_based.sub_pos_map)) {
      if (subPosValue.pos_logic_type === MethodologyEnum.MONTHLY_GROWTH_RATE) {
        handleMonthlyGrowthPositions(subPosKey, subPosValue, true)
      }

      if (subPosValue.pos_logic_type === MethodologyEnum.MANUAL_DEC_INPUT) {
        handleManualDecInputPositions(subPosKey, subPosValue)
      }

      if (subPosValue.pos_logic_type === MethodologyEnum.FORMULA_BASED) {
        handleFormulaBasedSubPositions(subPosKey, subPosValue)
      }
    }
  }
}

export const handleFormulaBasedSubPositions = (positionKey: string, planPosValue: PlanPosItem) => {
  if (!planPosValue || planPosValue.pos_logic_type !== MethodologyEnum.FORMULA_BASED || !planPosValue.formula_based) {
    return
  }
  const rows = getSelectedPlanRowDataByFstmt(planPosValue.fstmt_type)
  const row = rows.find((r) => r.row_id === positionKey)

  if (!row) return

  const formulaBasedTokens = makeFormulaTokensFromPlanItem(planPosValue.formula_based.formula_based_input_list || [], row, true)
  row.formulaTokens = formulaBasedTokens
  row.calc_status = planPosValue.formula_based.status

  const valuesMap = planPosValue.formula_based?.pos_res_plan_map || {}
  const IS_PNL = planPosValue.fstmt_type === StcsFstmtEnum.SFSE_PNL
  const planningStore = createPlanningViewStore(useCtxStore().active_comp_id)
  const selectedPlan = planningStore.selectedPlanState
  if (!selectedPlan) return

  let valuesMapObj = IS_PNL ? selectedPlan.valuesMapPnl || {} : selectedPlan.valuesMapBs || {}
  const basAmountKey = moment(selectedPlan?.fpna_begin_date_string, 'YYYY-MM').subtract(1, 'months').format('YYYY-MM')

  valuesMapObj = {
    ...valuesMapObj,
    [positionKey]: {
      values_map: {
        [basAmountKey]: planPosValue.formula_based?.pos_input_base_amount || 0,
        ...valuesMap,
      },
    },
  }

  if (IS_PNL) {
    selectedPlan.valuesMapPnl = valuesMapObj
  } else {
    selectedPlan.valuesMapBs = valuesMapObj
  }
}

const handleManualDecInputPositions = (positionKey: string, planPosValue: PlanPosItem) => {
  const valuesMap = planPosValue.manual_dec_input?.pos_res_plan_map || {}
  const planningStore = createPlanningViewStore(useCtxStore().active_comp_id)
  const selectedPlan = planningStore.selectedPlanState
  if (!selectedPlan) return

  const IS_PNL = planPosValue.fstmt_type === StcsFstmtEnum.SFSE_PNL

  if (IS_PNL) {
    selectedPlan.valuesMapPnl = {
      ...selectedPlan.valuesMapPnl,
      [positionKey]: {
        values_map: valuesMap,
      },
    }
  } else {
    selectedPlan.valuesMapBs = {
      ...selectedPlan.valuesMapBs,
      [positionKey]: {
        values_map: valuesMap,
      },
    }
  }
}

// Converts the backend formula_based_input_list into frontend FormulaTokens
const makeFormulaTokensFromPlanItem = (formulaBasedInputList: PlanItemPosFormulaBased['formula_based_input_list'], row: PlanningRowData, isSubPos = false): FormulaToken[] => {
  const formulaTokens: FormulaToken[] = []

  for (const item of formulaBasedInputList) {
    // Handle mathematical operators and text
    if (item.math_op) {
      formulaTokens.push(createTextToken(item.math_op))
      continue
    }

    // Handle level-2 position references (cross-parent references)
    if (item.plan_ref_level_2_position) {
      const token = createLevel2PositionReferenceToken(item.plan_ref_level_2_position)
      formulaTokens.push(token)
      continue
    }

    // Handle position references (same-parent references)
    if (item.plan_ref_position) {
      const token = createPositionReferenceToken(item.plan_ref_position)
      formulaTokens.push(token)
      continue
    }

    // Handle level-1/level-2/level-3/total references
    if (item.plan_ref_fstmt) {
      const token = createLevel2ReferenceToken(item.plan_ref_fstmt)
      formulaTokens.push(token)
      continue
    }

    // Handle sub position references
    if (item.plan_ref_sub_position) {
      const token = createSubPositionReferenceToken(item.plan_ref_sub_position)
      formulaTokens.push(token)
      continue
    }

    // Handle self-referential references
    if (item.plan_ref_self_position) {
      const token = createSelfReferenceToken(item.plan_ref_self_position, row, isSubPos)
      formulaTokens.push(token)
    }
  }

  return formulaTokens
}

//--------------------- UTILITIES PART -----------------------------

const getParentRowId = (path_level: string[]): string | null => {
  if (!path_level || path_level.length < 2) {
    return null
  }
  // Parent is the second-to-last element in path_level
  // (last element is the row's own row_id)
  return path_level[path_level.length - 2]
}

const getRowByLevelId = (statement: StcsFstmtEnum, rowIdOrLevelId: string): PlanningRowData | null => {
  const { getRowsDataByFstmt } = useFormulaData()
  const rows = getRowsDataByFstmt(statement)
  const row = rows.find((r) => r.row_id.endsWith(rowIdOrLevelId)) || null
  return row || null
}

const getDisplayNameForReference = (statement: StcsFstmtEnum, levelId: string): string => {
  const row = getRowByLevelId(statement, levelId)
  if (row) {
    return getRowDisplayName(row).name
  }
  consola.warn(`Could not find display name for reference: ${levelId}`)
  return levelId
}

const formatOffsetDisplayText = (offsetInMonths: number): string => {
  if (offsetInMonths === 0) {
    return 'This Month'
  }
  if (offsetInMonths === -1) {
    return 'Previous Month'
  }
  if (offsetInMonths < 0) {
    return `${Math.abs(offsetInMonths)} Months Ago`
  }
  if (offsetInMonths > 0) {
    return `+${offsetInMonths} Months`
  }
  return 'This Month'
}

const createTextToken = (text: string): FormulaToken => ({
  type: 'text',
  text,
})

const createLevel2PositionReferenceToken = (ref: NonNullable<PlanItemPosFormulaBased['formula_based_input_list'][number]['plan_ref_level_2_position']>): FormulaToken => {
  const displayName = getDisplayNameForReference(ref.fstmt_type, ref.ref_id_plan_position)
  const offsetDisplayText = formatOffsetDisplayText(ref.offset_in_months)
  const operandRow = getRowByLevelId(ref.fstmt_type, ref.ref_id_plan_position)

  return {
    type: 'operand',
    chip: {
      chipId: `chip-${uuidv7()}`,
      operand: {
        id: ref.ref_id_plan_position,
        displayName,
        display_type: 'planning',
        fstmt_type: ref.fstmt_type,
        level_id: operandRow?.level_id || '',
        isSelfReferential: false,
        fpna_type: operandRow?.fpna_type || StcsFpnaTypeEnum.SFT_LEVEL_2,
      },
      offset: createOffsetConfig(ref.offset_in_months, offsetDisplayText),
    },
  }
}

const createPositionReferenceToken = (ref: NonNullable<PlanItemPosFormulaBased['formula_based_input_list'][number]['plan_ref_position']>): FormulaToken => {
  const displayName = getDisplayNameForReference(ref.fstmt_type, ref.ref_id_plan_position)
  const offsetDisplayText = formatOffsetDisplayText(ref.offset_in_months)

  return {
    type: 'operand',
    chip: {
      chipId: `chip-${uuidv7()}`,
      operand: {
        id: ref.ref_id_plan_position,
        displayName,
        display_type: 'planning',
        fstmt_type: ref.fstmt_type,
        level_id: getRowByLevelId(ref.fstmt_type, ref.ref_id_plan_position)?.level_id || '',
        isSelfReferential: false,
        fpna_type: getRowByLevelId(ref.fstmt_type, ref.ref_id_plan_position)?.fpna_type || StcsFpnaTypeEnum.SFT_LEVEL_2,
      },
      offset: createOffsetConfig(ref.offset_in_months, offsetDisplayText),
    },
  }
}

const createLevel2ReferenceToken = (ref: NonNullable<PlanItemPosFormulaBased['formula_based_input_list'][number]['plan_ref_fstmt']>): FormulaToken => {
  const displayName = getDisplayNameForReference(ref.fstmt_type, ref.ref_id_level)
  const offsetDisplayText = formatOffsetDisplayText(ref.offset_in_months)
  const { getRowFpnaType } = useRowUtils()
  return {
    type: 'operand',
    chip: {
      chipId: `chip-${uuidv7()}`,
      operand: {
        id: getRowByLevelId(ref.fstmt_type, ref.ref_id_level)?.row_id || '',
        displayName,
        display_type: getRowFpnaType(ref.fpna_type),
        fstmt_type: ref.fstmt_type,
        level_id: ref.ref_id_level,
        isSelfReferential: false,
        fpna_type: ref.fpna_type,
      },
      offset: createOffsetConfig(ref.offset_in_months, offsetDisplayText),
    },
  }
}

const createSubPositionReferenceToken = (ref: NonNullable<PlanItemPosFormulaBased['formula_based_input_list'][number]['plan_ref_sub_position']>): FormulaToken => {
  const displayName = getDisplayNameForReference(ref.fstmt_type, ref.ref_id_plan_sub_position)
  const offsetDisplayText = formatOffsetDisplayText(ref.offset_in_months)

  return {
    type: 'operand',
    chip: {
      chipId: `chip-${uuidv7()}`,
      operand: {
        id: getRowByLevelId(ref.fstmt_type, ref.ref_id_plan_sub_position)?.row_id || '',
        displayName,
        display_type: 'subposition',
        fstmt_type: ref.fstmt_type,
        level_id: getRowByLevelId(ref.fstmt_type, ref.ref_id_plan_sub_position)?.level_id || '',
        isSelfReferential: false,
        fpna_type: getRowByLevelId(ref.fstmt_type, ref.ref_id_plan_sub_position)?.fpna_type || StcsFpnaTypeEnum.SFT_LEVEL_2,
      },
      offset: createOffsetConfig(ref.offset_in_months, offsetDisplayText),
    },
  }
}

const createSelfReferenceToken = (ref: NonNullable<PlanItemPosFormulaBased['formula_based_input_list'][number]['plan_ref_self_position']>, row: PlanningRowData, isSubPos = false): FormulaToken => {
  const offsetDisplayText = formatOffsetDisplayText(ref.offset_in_months)

  return {
    type: 'operand',
    chip: {
      chipId: `chip-${uuidv7()}`,
      operand: {
        id: row.row_id,
        displayName: getRowDisplayName(row).name,
        display_type: isSubPos ? 'subposition' : 'planning',
        fstmt_type: row.fstmt_type,
        level_id: row.level_id,
        isSelfReferential: true,
        fpna_type: row.fpna_type,
      },
      offset: createOffsetConfig(ref.offset_in_months, offsetDisplayText),
    },
  }
}

const createOffsetConfig = (offsetInMonths: number, displayText: string): FormulaOffset => ({
  type: offsetInMonths === 0 ? 'this_month' : offsetInMonths === -1 ? 'previous_month' : 'custom',
  customMonths: offsetInMonths,
  displayText,
})

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
export function convertValueBetweenOutputTypes(storedValue: number, oldType: StcsDisplayValueTypeEnum, newType: StcsDisplayValueTypeEnum, scaleFactor: number, invertFactor: number): number {
  let convertedValue = storedValue

  // Step 1: Convert from stored format to base value (what user sees)
  if (oldType === StcsDisplayValueTypeEnum.SDVTE_PERCENTAGE) {
    // Stored: 0.02, need base value for conversion
    convertedValue = storedValue // Already in base form (0.02)
  } else if (oldType === StcsDisplayValueTypeEnum.SDVTE_CURRENCY) {
    // Stored: value was scaled and inverted
    // To get original: divide by (scaleFactor * invertFactor)
    convertedValue = storedValue / (scaleFactor * invertFactor)
  } else if (oldType === StcsDisplayValueTypeEnum.SDVTE_ABSOLUTE) {
    // Stored: raw value
    convertedValue = storedValue
  }

  // Step 2: Convert base value to new storage format
  if (newType === StcsDisplayValueTypeEnum.SDVTE_PERCENTAGE) {
    // Keep as decimal (e.g., if absolute was 2, store as 0.02)
    // If converting from currency, the base value represents the actual number
    // We need to interpret it as percentage: e.g., 200 EUR → 2% → 0.02
    if (oldType === StcsDisplayValueTypeEnum.SDVTE_CURRENCY) {
      // Currency value like 200 EUR should become 2% (0.02)
      convertedValue /= 100
    } else if (oldType === StcsDisplayValueTypeEnum.SDVTE_ABSOLUTE) {
      // Absolute value like 2 should become 2% (0.02)
      convertedValue /= 100
    }
  } else if (newType === StcsDisplayValueTypeEnum.SDVTE_CURRENCY) {
    // Apply scaling and inversion
    if (oldType === StcsDisplayValueTypeEnum.SDVTE_PERCENTAGE) {
      // 0.02 (2%) → should become 2 EUR after scaling
      convertedValue = convertedValue * 100 * scaleFactor * invertFactor
    } else if (oldType === StcsDisplayValueTypeEnum.SDVTE_ABSOLUTE) {
      // Absolute 2 → 2 EUR after scaling
      convertedValue = convertedValue * scaleFactor * invertFactor
    }
  } else if (newType === StcsDisplayValueTypeEnum.SDVTE_ABSOLUTE) {
    // Store raw value
    if (oldType === StcsDisplayValueTypeEnum.SDVTE_PERCENTAGE) {
      // 0.02 (2%) → absolute 2
      convertedValue *= 100
    }
    // else if SDVTE_CURRENCY: convertedValue already set correctly in Step 1
  }

  return convertedValue
}

export function convertValuesMap(valuesMap: Record<string, number>, oldType: StcsDisplayValueTypeEnum, newType: StcsDisplayValueTypeEnum, scaleFactor: number, invertFactor: number, selectedMethod?: MethodologyEnum): Record<string, number> {
  const convertedValues: Record<string, number> = {}

  Object.entries(valuesMap).forEach(([monthKey, storedValue]) => {
    if (typeof storedValue !== 'number') return

    let convertedValue = storedValue

    if (selectedMethod === MethodologyEnum.FORMULA_BASED) {
      // For formula-based, only do conversion for base month (previous month of plan start)
      const planningStore = createPlanningViewStore(useCtxStore().active_comp_id)
      const selectedPlan = planningStore.selectedPlanState
      if (selectedPlan) {
        const baseMonthKey = moment(selectedPlan?.fpna_begin_date_string, 'YYYY-MM').subtract(1, 'months').format('YYYY-MM')
        if (monthKey !== baseMonthKey) {
          // For other months, keep the stored value as is
          convertedValues[monthKey] = storedValue
          return
        }
      }
    }

    convertedValue = convertValueBetweenOutputTypes(storedValue, oldType, newType, scaleFactor, invertFactor)

    convertedValues[monthKey] = convertedValue

    const oldTypeName = oldType !== undefined ? StcsDisplayValueTypeEnum[oldType] : 'undefined'
    const newTypeName = StcsDisplayValueTypeEnum[newType]
    consola.debug(`🔄 Output type conversion: ${oldTypeName} → ${newTypeName}`, `Month: ${monthKey}, Old stored: ${storedValue}, New stored: ${convertedValue}`)
  })

  return convertedValues
}
